<template>
  <div class="content">
    <van-nav-bar :fixed="true" z-index="9" :title="$t('goodsPage.title')" :border="false" left-arrow
      @click-left="onClickLeft" />
    <div v-if="detailLoading" class="android-goods-detail android-goods-skeleton" aria-busy="true">
      <section class="android-goods-skeleton__hero">
        <span class="android-goods-skeleton__shine"></span>
      </section>
      <section class="android-goods-skeleton__price">
        <div class="android-goods-skeleton__line android-goods-skeleton__line--title"></div>
        <div class="android-goods-skeleton__line android-goods-skeleton__line--label"></div>
        <div class="android-goods-skeleton__line android-goods-skeleton__line--price"></div>
      </section>
      <section class="android-goods-skeleton__card">
        <div class="android-goods-skeleton__row">
          <span class="android-goods-skeleton__icon"></span>
          <span class="android-goods-skeleton__line android-goods-skeleton__line--medium"></span>
        </div>
        <div class="android-goods-skeleton__divider"></div>
        <div class="android-goods-skeleton__line android-goods-skeleton__line--full"></div>
      </section>
      <section class="android-goods-skeleton__card">
        <div class="android-goods-skeleton__row">
          <span class="android-goods-skeleton__icon"></span>
          <span class="android-goods-skeleton__line android-goods-skeleton__line--medium"></span>
        </div>
        <div class="android-goods-skeleton__divider"></div>
        <div class="android-goods-skeleton__line android-goods-skeleton__line--full"></div>
        <div class="android-goods-skeleton__line android-goods-skeleton__line--short"></div>
      </section>
      <section class="android-goods-skeleton__tabs">
        <div class="android-goods-skeleton__tab-row">
          <span></span>
          <span></span>
        </div>
        <div class="android-goods-skeleton__image-line"></div>
        <div class="android-goods-skeleton__image-line android-goods-skeleton__image-line--small"></div>
      </section>
    </div>
    <div v-else-if="detailLoadError" class="android-goods-state">
      <ProductPageState type="error" :title="$t('systemError')" compact />
      <button type="button" class="android-goods-state__retry" @click="initData">{{ $t('retry') }}</button>
    </div>
    <div class="android-goods-detail" v-if="!detailLoading && !detailLoadError">
      <section class="android-goods-hero">
        <swipe :autoplay="3000" :show-indicators="false" :touchable="swipeList.length > 1" @change="onChange">
          <swipe-item v-for="(item, index) in swipeList" :key="`${item}-${index}`">
            <div class="android-goods-hero__image" v-lazy:background-image="item"></div>
          </swipe-item>
          <template #indicator>
            <div v-if="swipeList.length > 1" class="android-goods-indicator">
              <span v-for="(item, index) in swipeList" :key="`${item}-indicator-${index}`"
                class="android-goods-indicator__item" :class="{ active: index === currentIndex }"></span>
            </div>
          </template>
        </swipe>
      </section>

      <section class="android-goods-price-card">
        <span class="android-goods-price-card__watermark" aria-hidden="true"></span>
        <div class="android-goods-price-card__name">{{ product.name || '' }}</div>
        <div class="android-goods-price-card__label">{{ $t('orderPage.firstRentalAmount') }}</div>
        <div class="android-goods-price-card__price">
          <span>{{ $t('priceUnit') }}</span>
          <strong>{{ selectedSkuObj.price | numberFormat(0) }}</strong>
        </div>
      </section>

      <section class="android-goods-info-card" @click="openSkuSelectPopup">
        <div class="android-goods-info-card__header">
          <van-icon name="phone-o" />
          <span>{{ $t('product') }}</span>
          <van-icon name="arrow" />
        </div>
        <div class="android-goods-info-card__divider"></div>
        <div class="android-goods-info-card__value android-goods-info-card__value--ellipsis">{{ selectedSkuObj.selected }}</div>
      </section>

      <section class="android-goods-info-card" @click="openAddressPopup">
        <div class="android-goods-info-card__header">
          <van-icon name="location-o" />
          <span>{{ shippingAddressLabel }}</span>
          <van-icon name="arrow" />
        </div>
        <div class="android-goods-info-card__divider"></div>
        <div v-if="selectedAddress" class="android-goods-info-card__address">
          <div>{{ selectedAddress.recipientName }}</div>
          <div>{{ addressPhone }}</div>
          <div>{{ addressDetail }}</div>
        </div>
        <div v-else class="android-goods-info-card__value">{{ $t('select') }}</div>
      </section>

      <section class="android-goods-tabs">
        <tabs background="#FFFFFF" title-active-color="#0B45FF" title-inactive-color="#6B7280">
          <tab :title="$t('description')">
            <div class="tab-content-img">
              <template v-if="goodsDetail.images && Array.isArray(goodsDetail.images.detailed)">
                <img v-for="(value, index) in goodsDetail.images.detailed" :key="`${value}-${index}`" v-lazy="value" />
              </template>
            </div>
          </tab>
          <tab :title="$t('lease')">
            <div class="tab-content">
              <Lease></Lease>
            </div>
          </tab>
        </tabs>
      </section>
      <ProductBenefitSummary />
    </div>
    <div class="android-goods-footer" v-if="!detailLoading && !detailLoadError">
      <button type="button" class="android-goods-footer__support" @click="openCustomerSupport"
        :aria-label="$t('customerServicePop.whatsapp')">
        <img src="@/assets/img/home-alignment/whatsapp.svg" :alt="$t('customerServicePop.whatsapp')">
      </button>
      <button type="button" class="android-goods-footer__apply" :class="{ 'is-disabled': isApplyDisabled }"
        :disabled="isApplyDisabled" @click="goOrderConfirm">
        {{ isApplyDisabled && isDisabled ? $t('unavailable') : $t('productCommon.applyNow') }}
      </button>
    </div>

    <SkuSelectPopup v-if="!detailLoading && !detailLoadError && skuList.length" ref="skuSelectPopup" :product="product" :preferredSkuId="preferredSkuId" @confirm="onSkuConfirm"
      @defaultChange="onSkuConfirm"></SkuSelectPopup>

    <AddressPopup v-if="!detailLoading && !detailLoadError" :key="componentKey" ref="addressPopup" @change="onAddressChange" @delete="onAddressDelete"
      :defaultSelectId="selectedAddress && selectedAddress.id">
    </AddressPopup>

  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import { Swipe, SwipeItem, Tab, Tabs, Dialog, Icon } from 'vant';
import SkuSelectPopup from '@/components/SkuSelectPopup/index.vue';
import AddressPopup from '@/components/AddressPopup/index.vue';
import Lease from '@/components/Lease.vue';
import ProductBenefitSummary from '@/components/ProductBenefitSummary/index.vue';
import ProductPageState from '@/components/ProductPageState/index.vue';
import { getGoodsDetail } from '@/api/goods';
import routerBack from '@/mixins/routerBack';
import { formatAddressForOrder } from '@/utils/address';
import { buildCustomerSupportWhatsAppUrl } from '@/common/customerSupport';
import { DEFAULT_REG_CHANNEL } from '@/config';
export default {
  mixins: [routerBack],
  components: {
    Swipe,
    SwipeItem,
    Tab,
    Tabs,
    Icon,
    'van-icon': Icon,
    SkuSelectPopup,
    AddressPopup,
    Lease,
    ProductBenefitSummary,
    ProductPageState
  },
  data() {
    return {
      componentKey: Math.random(),
      goodsId: '',
      preferredSkuId: '',
      currentIndex: 0,
      selectedSku: {},
      skuImageOverride: '',
      selectedAddress: null,
      goodsDetail: {},
      orderSubmitting: false,
      detailLoading: true,
      detailLoadError: false,
    };
  },
  computed: {
    ...mapState({
      isLogin: state => state.user.session?.accessToken || '',
      addressList: state => state.user.addressList || [],
      channel: state => state.user.channel || DEFAULT_REG_CHANNEL
    }),
    addressPhone() {
      if (!this.selectedAddress?.recipientPhone) return '';
      const normalized = String(this.selectedAddress.recipientPhone).replace(/\D/g, '');
      const local = normalized.startsWith('62')
        ? normalized.slice(2)
        : normalized.replace(/^0/, '');
      if (!local) return '';
      return `+62 ${local.slice(0, 3)} ${local.slice(3, 7)} ${local.slice(7)}`.trim();
    },
    addressDetail() {
      if (!this.selectedAddress) {
        return '';
      }
      return formatAddressForOrder(this.selectedAddress);
    },
    shippingAddressLabel() {
      const localeMessages = this.$i18n.getLocaleMessage(this.$i18n.locale) || {};
      const localizedLabel = localeMessages.goodsPage?.shippingAddress;
      if (typeof localizedLabel === 'string') return localizedLabel;

      const fallbackMessages = this.$i18n.getLocaleMessage('en') || {};
      const fallbackLabel = fallbackMessages.goodsPage?.shippingAddress;
      return typeof fallbackLabel === 'string' ? fallbackLabel : 'Shipping Address';
    },
    showType() {
      return this.addressList?.length > 0 ? 2 : 1;
    },
    skuList() {
      return Array.isArray(this.goodsDetail?.sku) ? this.goodsDetail.sku : [];
    },
    selectedSkuObj() {
      let selected = '';
      let price = '';
      if (this.selectedSku?.sku) {
        selected = this.selectedSkuLabel;
        price = this.addMoney(this.selectedSku.sku.price, this.selectedSku.sku.riskPrice);
      } else {
        selected = this.selectedSkuLabel || this.$t('select') || '';
        const sku = this.skuList[0];
        price = this.addMoney(sku?.price, sku?.riskPrice) || '0.00';

      }
      return {
        selected,
        price,
      };
    },
    selectedSkuLabel() {
      const sku = this.selectedSku?.sku || this.skuList[0];
      if (!sku) return '';
      const values = (this.goodsDetail?.attributes || []).map(attribute => {
        const selectedValue = this.selectedSku?.selectedSpecs?.[attribute.attrKey];
        const rawValue = selectedValue !== undefined
          ? selectedValue
          : sku.attributes?.[attribute.attrKey];
        if (rawValue === undefined || rawValue === null || rawValue === '') return '';
        return String(rawValue);
      }).filter(Boolean);
      return values.join(' • ') || this.selectedSku?.selectedSkuStr || '';
    },
    swipeList() {
      const carousel = Array.isArray(this.goodsDetail?.images?.carousel)
        ? this.goodsDetail.images.carousel
        : [];
      const skuImage = this.skuImageOverride || '';
      const fallback = this.goodsDetail?.images?.thumbnail || '';
      const detailedFallback = Array.isArray(this.goodsDetail?.images?.detailed)
        ? this.goodsDetail.images.detailed.slice(0, 1)
        : [];
      const baseImages = carousel.length ? carousel : [fallback, ...detailedFallback];
      const images = baseImages
        .filter(Boolean)
        .filter((item, index, list) => list.indexOf(item) === index);
      if (!skuImage) return images;
      if (!images.length) return [skuImage];
      if (images.includes(skuImage)) return images;
      return [skuImage, ...images];
    },
    product() {
      let { attributes = [], goodsInfo = {}, images = {} } = this.goodsDetail;
      const sku = this.skuList;
      return {
        id: goodsInfo.id,
        name: goodsInfo.goodName || '',
        goodDescription: goodsInfo.goodDescription || '',
        price: this.addMoney(sku[0]?.price, sku[0]?.riskPrice), // 默认首期金额
        riskPrice: sku[0]?.riskPrice || 0,
        depositPrice: sku[0]?.depositPrice || 0,
        thumbnail: images?.thumbnail || '',
        specs: attributes,
        skus: sku
      }
    },
    isDisabled() {
      return this.goodsDetail?.goodsInfo?.status === 'DOWN'
    },
    isApplyDisabled() {
      return this.isDisabled || !this.selectedSku?.sku || Number(this.selectedSku.sku.stock) <= 0;
    }
  },
  mounted() {
    this.goodsId = this.$route.query.goodsId || '';
    this.preferredSkuId = this.$route.query.skuId || '';
    this.initData();
  },
  watch: {
    '$route.query.skuId'(value) {
      this.preferredSkuId = value || '';
    },
    '$route.query.goodsId'(value) {
      const nextGoodsId = value || '';
      if (nextGoodsId === this.goodsId) return;
      this.goodsId = nextGoodsId;
      this.preferredSkuId = this.$route.query.skuId || '';
      this.selectedSku = {};
      this.skuImageOverride = '';
      this.currentIndex = 0;
      this.goodsDetail = {};
      this.initData();
    },
  },
  methods: {
    ...mapActions('user', ['getAddressList', 'clearLogin']),
    ...mapActions('orders', ['createTempOrder']),
    addMoney(...amounts) {
      return amounts.reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
    },
    async initData() {
      if (!this.goodsId) {
        this.detailLoadError = true;
        return;
      }
      this.detailLoading = true;
      this.detailLoadError = false;
      try {
        const res = await getGoodsDetail({ goodsId: this.goodsId });
        if (res?.code === 'Err.GoodsNotExist') {
          this.$toast(res?.message || this.$t('systemError'));
          window.setTimeout(() => {
            this.backAfterGoodsNotExist();
          }, 800);
          return;
        }
        if (res?.code !== 'Success') {
          throw new Error(res?.message || 'get goods detail failed');
        }
        this.goodsDetail = res.data;
        this.currentIndex = 0;
        this.skuImageOverride = '';
        this.selectedSku = this.getDefaultSkuSelection();
        if (this.isLogin) await this.loadAddressList();
      } catch (e) {
        this.detailLoadError = true;
        this.$toast(this.$t('systemError'));
      } finally {
        this.detailLoading = false;
      }
    },
    async loadAddressList() {
      try {
        const addressResult = await this.getAddressList({ _reqLogin: false });
        if (addressResult?.code !== 'Success') {
          if ([401, 403].includes(addressResult?.httpStatus)) await this.clearLogin();
          throw new Error(addressResult?.message || 'get address list failed');
        }
        const responseAddresses = Array.isArray(addressResult?.data?.records)
          ? addressResult.data.records
          : [];
        const addresses = responseAddresses.length ? responseAddresses : this.addressList;
        this.selectedAddress = this.findDefaultAddress(addresses) || null;
        return true;
      } catch (e) {
        this.selectedAddress = null;
        return false;
      }
    },
    isTruthyDefaultFlag(value) {
      return value === true || value === 1 || value === '1' || value === 'true';
    },
    isDefaultAddress(address) {
      if (!address) return false;
      return [
        address.isDefault,
        address.idDefault,
        address.idDefualt,
        address.is_default,
        address.id_default,
      ].some(value => this.isTruthyDefaultFlag(value))
        || address.idDefault === address.id
        || address.idDefualt === address.id;
    },
    findDefaultAddress(addresses) {
      return addresses.find(address => this.isDefaultAddress(address)) || addresses[0];
    },
    onClickLeft() {
      this.goBack()
    },
    backAfterGoodsNotExist() {
      const historyKey = 'pageHistory';
      let history = [];
      try {
        history = JSON.parse(sessionStorage.getItem(historyKey) || '[]');
      } catch (error) {
        history = [];
      }

      let target = '';
      while (history.length) {
        const prevPage = history.pop();
        const prevPath = String(prevPage || '').split('?')[0];
        if (prevPath && prevPath !== '/goods/detail') {
          target = prevPage;
          break;
        }
      }

      sessionStorage.setItem(historyKey, JSON.stringify(history));
      sessionStorage.setItem('isGoBack', 'true');
      this.$router.replace(target || '/index');
    },
    onChange(index) {
      this.currentIndex = index;
    },
    openSkuSelectPopup() {
      this.$refs.skuSelectPopup.open();
    },
    onSkuConfirm(data) {
      console.log('确认选择的SKU:', data);
      this.selectedSku = data;
      this.skuImageOverride = data?.sku?.imageUrl || '';
      const selectedImageIndex = this.skuImageOverride
        ? this.swipeList.indexOf(this.skuImageOverride)
        : -1;
      this.currentIndex = selectedImageIndex >= 0 ? selectedImageIndex : 0;
    },
    openAddressPopup() {
      if (!this.isLogin) {
        this.confirmLoginRequired();
        return;
      }
      this.$refs.addressPopup.open({
        showType: this.showType
      });
    },
    onAddressChange({ data }) {
      console.log('确认选择的地址:', data);
      this.selectedAddress = data;
    },
    onAddressDelete({ data }) {
      console.log('删除地址:', data);
      if (this.selectedAddress?.id === data.id) {
        this.selectedAddress = this.findDefaultAddress(this.addressList) || null;
        this.$nextTick(() => {
          this.componentKey = Math.random();
        })
      }
    },
    goLogin() {
      this.$router.replace({
        path: '/login',
        query: {
          redirect: this.$route.fullPath
        }
      });
    },
    confirmLoginRequired() {
      Dialog.confirm({
        title: this.$t('myPage.loginRequiredTitle'),
        message: this.$t('myPage.loginRequiredContent'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      })
        .then(() => {
          this.goLogin();
        })
        .catch(() => {
        });
    },
    getDefaultSkuSelection() {
      const skus = this.skuList;
      const preferredId = String(this.preferredSkuId || '');
      const sku = skus.find(item => String(item.id ?? item.goodsSkuId ?? '') === preferredId && Number(item.stock) > 0)
        || skus.find(item => Number(item.stock) > 0)
        || skus[0];
      if (!sku) return {};
      const selectedSpecs = {};
      (this.goodsDetail.attributes || []).forEach(attribute => {
        const value = sku.attributes?.[attribute.attrKey];
        if (value !== undefined && value !== null && value !== '') {
          selectedSpecs[attribute.attrKey] = value;
        }
      });
      return {
        sku,
        selectedSpecs,
        selectedSkuStr: '',
      };
    },
    async goOrderConfirm() {
      if (this.isDisabled || this.orderSubmitting) {
        return;
      }
      if (!this.isLogin) {
        this.confirmLoginRequired();
        return;
      }
      if (!this.selectedSku?.sku) {
        let stock = this.product.skus.reduce((total, item) => total + item.stock, 0);
        if(stock > 0){
          this.$toast(this.$t('skuPop.noSelect'));
        }

        return;
      }
      if (!this.selectedAddress?.id) {
        this.$toast({ message: this.$t('addressPop.noSelectAddress'), duration: 2000 });
        window.setTimeout(() => {
          this.$refs.addressPopup?.open({
            showType: this.showType
          });
        }, 2000);
        return;
      }
      this.orderSubmitting = true;
      try {
        await this.createTempOrder({
          address: this.selectedAddress,
          sku: this.selectedSku?.sku || {},
          skuStr: this.selectedSku?.selectedSkuStr || '',
          goodsDetail: this.goodsDetail,
        });
        this.$router.push({ name: 'OrderConfirm' });
      } finally {
        this.orderSubmitting = false;
      }
    },
    openCustomerSupport() {
      window.open(buildCustomerSupportWhatsAppUrl(this.channel), '_blank', 'noopener,noreferrer');
    },
  },
};
</script>
<style lang="less" scoped>
.content {
  background: #F6FBFF;
  min-height: 100vh;
  padding-top: 88px;
  padding-bottom: 1.24rem;
  box-sizing: border-box;

  /deep/ .van-tabs__nav--line {
    box-sizing: border-box;
  }
}

.goods-detail {
  &__img {
    background: #ffffff;
    width: 750px;
    height: 850px;
  }
}

.banner-img {
  padding-bottom: 100px;

  &__img-wrapper {
    width: 100%;
    height: 0;
    padding-bottom: 100%;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  }
}

.custom-indicator {
  position: absolute;
  left: 0;
  bottom: 80px;
  width: 100%;
  height: 50px;
  z-index: 9;
}

.indicator-item {
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius-base);
  background-color: var(--text-color-gray);
  opacity: 0.3;
  margin-right: 20px;
  transition: width 0.2s ease;
  margin-top: -40px;

  &:last-child {
    margin-right: 0;
  }

  &.active {
    width: 50px;
    opacity: 0.6;
  }
}

.detail-sku {
  margin-top: -100px;
  padding: 40px 30px 113px 30px;
  background: linear-gradient(270deg, #4C7DF9 0%, #2547F1 100%);
  border-radius: 50px;
  filter: blur(0px);
  text-align: left;

  &__brand {
    font-size: 42px;
    color: #FFFFFF;
    line-height: 48px;
    font-weight: 500;
  }

  &__title {
    font-size: 26px;
    color: rgba(255, 255, 255, 0.6);
    line-height: 31px;
    word-wrap: break-word;
  }

  &__price {
    display: flex;
    align-items: baseline;
    margin-top: 4px;

    .unit {
      font-size: 36px;
      color: rgba(255, 255, 255, 0.3);
      line-height: 42px;
    }

    .price {
      font-weight: 500;
      font-size: 64px;
      color: #FFFFFF;
      line-height: 76px;
    }
  }

  &__price-label {
      margin-top: 22px;
      font-size: 30px;
      line-height: 36px;
      color: #FFFFFF;
  }
}

.detail-body {
  position: relative;
  z-index: 1;
  margin-top: -92px;
}

.tab-content {
  padding: 20px 30px;
  font-size: 26px;
  color: var(--text-color-gray);
  line-height: 38px;
  text-align: left;

  img {
    display: block;
    width: 100%;
  }
}

.tab-content-img {
  padding: 20px 0;

  img {
    display: block;
    width: 100%;
    vertical-align: middle;
  }
}

.detail-sku-selection {
  background-color: #FFFFFF;
  border-radius: var(--border-radius-base);
  box-shadow: 0px 8px 30px 0px rgba(0, 0, 0, 0.08);
  /* 添加下阴影 */

  >.flex {
    padding: 24px 30px;
    font-size: 32px;
    line-height: 37px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 200%;
      height: 1px;
      transform: scale(0.5);
      background-color: var(--border-color-base);
      margin-left: -50%;
    }

    &:last-child::after {
      display: none;
    }

    background: url(../../assets/img/next.png) right 30px center no-repeat;
    background-size: auto 24px;

    .f-c-999 {
      padding-right: 30px;
      word-break: break-all;
    }
  }

  .text-1 {
    word-break: keep-all;
    width: 180px;
  }
}

.btn-warp {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 99;
  padding: 30px 30px 40px 30px;
  background: #F6FBFF;
}
</style>

<style lang="less" scoped>
.content {
  padding-top: 92px;
  padding-bottom: 1.24rem;
  background: var(--bg-color);

  ::v-deep .van-nav-bar {
    height: 92px;
    background: #fff;
  }

  ::v-deep .van-nav-bar__title { color: #172554; font-size: 30px; font-weight: 600; }
  ::v-deep .van-nav-bar .van-icon { color: #172554; font-size: 40px; }
}

.goods-detail__img {
  width: 750px;
  max-width: 100%;
  height: 750px;
  background: #fff;
}

.banner-img { padding-bottom: 0; }
.banner-img__img-wrapper { height: 750px; padding-bottom: 0; }

.detail-sku {
  margin: 16px 16px 0;
  padding: 16px;
  border: 2px solid #E5EAF3;
  border-radius: 20px;
  background: #fff;
  text-align: left;

  &__brand { color: #3155A5; font-size: 30px; line-height: 44px; font-weight: 600; }
  &__title { margin-top: 8px; color: #172554; font-size: 30px; line-height: 44px; }
  &__price-label { margin-top: 16px; color: #64748B; font-size: 24px; line-height: 32px; }
  &__price { margin-top: 4px; }
  &__price .unit { color: #172554; font-size: 26px; line-height: 36px; }
  &__price .price { margin-left: 8px; color: #172554; font-size: 42px; line-height: 52px; font-weight: 600; }
}

.detail-body { margin: 16px; }
.detail-sku-selection {
  margin: 0;
  border: 2px solid #E5EAF3;
  border-radius: 20px;
  box-shadow: none;
  overflow: hidden;

  > .flex {
    padding: 16px;
    font-size: 26px;
    line-height: 36px;
    background-position: right 16px center;
    background-size: auto 32px;

    .f-c-999 { padding-right: 32px; }
  }
}

.detail-body > .mg-t-20 { margin-top: 16px; }
::v-deep .van-tabs { border: 2px solid #E5EAF3; border-radius: 20px; overflow: hidden; background: #fff; }
::v-deep .van-tabs__wrap { height: 88px; }
::v-deep .van-tab { color: #64748B; font-size: 26px; }
::v-deep .van-tab--active { color: #172554; font-weight: 600; }
.tab-content { padding: 16px; font-size: 26px; line-height: 36px; }
.tab-content-img { padding: 16px 0; }

.btn-warp {
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
  background: var(--bg-color);
  border-top: 2px solid #E5EAF3;
}

.btn-warp .btn-primary {
  height: 92px;
  border-radius: 16px;
  font-size: 26px;
  line-height: 88px;
}
</style>

<style lang="less" scoped>
.content {
  min-height: 100vh;
  padding-top: 92px;
  padding-bottom: 1.24rem;
  box-sizing: border-box;
  background: #F4F8FF;

  ::v-deep .van-nav-bar {
    height: 92px;
    background: #fff;
  }

  ::v-deep .van-nav-bar__title {
    color: #070D3F;
    font-size: 30px;
    font-weight: 600;
  }

  ::v-deep .van-nav-bar .van-icon {
    color: #070D3F;
    font-size: 40px;
  }
}

.android-goods-detail {
  padding: 16px 16px 24px;
  box-sizing: border-box;
}

.android-goods-state {
  box-sizing: border-box;
  padding: 28px 24px;

  ::v-deep .product-page-state {
    border-width: 2px;
    box-shadow: 0 10px 28px rgba(20, 36, 77, .05);
  }

  &__retry {
    display: block;
    width: 100%;
    height: 88px;
    margin-top: 24px;
    border: 0;
    border-radius: 16px;
    background: #0B45FF;
    color: #fff;
    font-size: 30px;
    line-height: 88px;
    font-weight: 400;
    text-align: center;
  }
}

.android-goods-skeleton {
  &__hero,
  &__price,
  &__card,
  &__tabs,
  &__line,
  &__icon,
  &__tab-row span,
  &__image-line {
    position: relative;
    overflow: hidden;
    background: #EAF1FB;
  }

  &__hero,
  &__price,
  &__card,
  &__tabs {
    border-radius: 20px;
    background: #fff;
  }

  &__hero {
    height: 546px;
    border-radius: 16px;
  }

  &__shine,
  &__line::after,
  &__icon::after,
  &__tab-row span::after,
  &__image-line::after,
  &__hero::after,
  &__price::after,
  &__card::after,
  &__tabs::after {
    position: absolute;
    inset: 0;
    content: '';
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, .72) 50%, transparent 100%);
    animation: goodsSkeletonShimmer 1.25s ease-in-out infinite;
    transform: translateX(-100%);
  }

  &__price {
    margin-top: 16px;
    padding: 26px 28px 28px;
  }

  &__card,
  &__tabs {
    margin-top: 16px;
    padding: 20px 24px;
    border: 2px solid #E5EAF3;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__icon {
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  &__line {
    height: 24px;
    border-radius: 999px;

    &--title {
      width: 72%;
      height: 38px;
    }

    &--label {
      width: 38%;
      height: 26px;
      margin-top: 18px;
    }

    &--price {
      width: 46%;
      height: 44px;
      margin-top: 12px;
    }

    &--medium {
      width: 42%;
      height: 30px;
    }

    &--full {
      width: 86%;
      margin-left: 52px;
    }

    &--short {
      width: 58%;
      margin-top: 14px;
      margin-left: 52px;
    }
  }

  &__divider {
    height: 2px;
    margin: 18px 0;
    background: #EEF2F7;
  }

  &__tab-row {
    display: flex;
    gap: 36px;
    justify-content: center;
    padding: 10px 0 28px;

    span {
      width: 124px;
      height: 32px;
      border-radius: 999px;
    }
  }

  &__image-line {
    height: 120px;
    border-radius: 16px;

    &--small {
      height: 76px;
      margin-top: 16px;
    }
  }
}

@keyframes goodsSkeletonShimmer {
  100% {
    transform: translateX(100%);
  }
}

.android-goods-hero {
  position: relative;
  height: 546px;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;

  ::v-deep .van-swipe,
  ::v-deep .van-swipe-item {
    height: 100%;
    touch-action: pan-y;
  }

  ::v-deep .van-swipe__track {
    touch-action: pan-y;
  }

  &__image {
    width: 100%;
    height: 100%;
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    touch-action: pan-y;
    user-select: none;
  }
}

.android-goods-indicator {
  position: absolute;
  right: 0;
  bottom: 16px;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &__item {
    display: block;
    width: 12px;
    height: 12px;
    border-radius: 999px;
    background: rgba(11, 69, 255, .24);
    transition: width .2s ease;

    &.active {
      width: 48px;
      background: #0B45FF;
    }
  }
}

.android-goods-price-card {
  position: relative;
  overflow: hidden;
  margin-top: 16px;
  padding: 22px 28px;
  border-radius: 20px;
  background: linear-gradient(135deg, #0B45FF 0%, #2563EB 100%);
  color: #fff;

  &__watermark {
    position: absolute;
    top: 50%;
    right: 28px;
    width: 112px;
    height: 128px;
    border: 10px solid rgba(255, 255, 255, .14);
    border-radius: 32px 32px 48px 48px;
    transform: translateY(-50%);

    &::after {
      position: absolute;
      top: 32px;
      left: 27px;
      width: 48px;
      height: 24px;
      border-bottom: 10px solid rgba(255, 255, 255, .14);
      border-left: 10px solid rgba(255, 255, 255, .14);
      content: '';
      transform: rotate(-45deg);
    }
  }

  &__name {
    position: relative;
    z-index: 1;
    max-width: 100%;
    overflow: hidden;
    color: #fff;
    font-size: 38px;
    line-height: 48px;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__label {
    position: relative;
    z-index: 1;
    margin-top: 12px;
    color: rgba(255, 255, 255, .9);
    font-size: 30px;
    line-height: 40px;
  }

  &__price {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: baseline;
    gap: 0;
    margin-top: 4px;

    span {
      font-size: 46px;
      line-height: 56px;
    }

    strong {
      font-size: 46px;
      line-height: 56px;
      font-weight: 400;
    }
  }
}

.android-goods-info-card {
  margin-top: 16px;
  padding: 16px;
  border: 2px solid #E5EAF3;
  border-radius: 20px;
  background: #fff;
  color: #070D3F;
  cursor: pointer;

  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
    min-height: 36px;
    color: #070D3F;
    font-size: 30px;
    line-height: 44px;
    font-weight: 600;

    > .van-icon:first-child {
      flex: 0 0 36px;
      color: #0B45FF;
      font-size: 36px;
    }

    > .van-icon:last-child {
      flex: 0 0 32px;
      margin-left: auto;
      color: #6B7280;
      font-size: 32px;
    }
  }

  &__divider {
    height: 2px;
    margin: 16px 0;
    background: #E5E7EB;
  }

  &__value,
  &__address {
    padding-left: 52px;
    color: #070D3F;
    font-size: 26px;
    line-height: 36px;
    font-weight: 500;
    word-break: break-word;
  }

  &__value--ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__address > div + div {
    margin-top: 8px;
  }
}

.android-goods-tabs {
  margin-top: 16px;
  overflow: hidden;
  background: transparent;

  ::v-deep .van-tabs__wrap {
    height: 88px;
    background: #F0F0F5;
  }

  ::v-deep .van-tab {
    font-size: 26px;
    font-weight: 400;
    color: #6B7280;
  }

  ::v-deep .van-tab--active {
    color: #0B45FF;
    font-weight: 400;
  }

  ::v-deep .van-tabs__line {
    width: 96px;
    height: 8px;
    border-radius: 999px;
    background: #0B45FF;
  }
}

.tab-content {
  padding: 16px;
  color: #697386;
  font-size: 26px;
  line-height: 36px;
  font-weight: 400;
  text-align: left;
}

.tab-content-img {
  padding: 16px 0;

  img {
    display: block;
    width: 100%;
    vertical-align: middle;
  }
}

.android-goods-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99;
  display: flex;
  gap: 24px;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  border-top: 2px solid #E5EAF3;
  background: #fff;

  &__support,
  &__apply {
    height: 92px;
    border-radius: 16px;
    font-size: 32px;
    line-height: 88px;
  }

  &__support {
    flex: 0 0 96px;
    width: 96px;
    padding: 0;
    border: 2px solid #DDE5F2;
    background: #fff;

    img {
      display: block;
      width: 48px;
      height: 48px;
      margin: auto;
    }
  }

  &__apply {
    flex: 1;
    min-width: 0;
    padding: 0 24px;
    border: 2px solid #0B45FF;
    background: #0B45FF;
    color: #fff;
    white-space: nowrap;

    &.is-disabled {
      border-color: rgba(11, 69, 255, .45);
      background: rgba(11, 69, 255, .45);
    }
  }
}

@media (min-width: 600px) {
  .android-goods-detail,
  .android-goods-footer {
    width: 600px;
    margin-right: auto;
    margin-left: auto;
  }

  .android-goods-footer {
    right: 50%;
    left: auto;
    transform: translateX(50%);
  }
}
</style>
